import type {
  EnvironmentId,
  EnvironmentRecoveryAction,
  ServerConfig,
} from "@afar-research/contracts";
import * as Option from "effect/Option";
import { AsyncResult, Atom } from "effect/unstable/reactivity";

import {
  AVAILABLE_CONNECTION_STATE,
  clientReadiness,
  type ClientReadiness,
  type SupervisorConnectionState,
} from "../connection/model.ts";
import type { HostEnvironmentEntry } from "../connection/host.ts";
import type { EnvironmentRuntimeState } from "./environments.ts";

export interface EnvironmentConnectionPresentation {
  readonly phase: ClientReadiness;
  readonly error: string | null;
  readonly traceId: string | null;
  readonly recoveryAction: EnvironmentRecoveryAction | null;
  /**
   * Whether the connection gets itself back without the user. `phase` cannot answer this:
   * `clientReadiness` folds a scheduled retry, a waiting-for-network client and a blocked
   * one into one `unavailable`, and a surface that cannot tell them apart either promises
   * a reconnection that will never come or hides one already on its way.
   */
  readonly willRetryAutomatically: boolean;
  /**
   * The endpoint exists but is not serving yet, and the connection keeps trying until it does,
   * as a Cloud Workspace's run does while it starts. That is still loading, not a failure.
   */
  readonly endpointStarting: boolean;
}

function recoveryAction(state: SupervisorConnectionState): EnvironmentRecoveryAction | null {
  const failure = state.lastFailure;
  if (failure === null) return null;
  if (failure._tag === "ConnectionTransientError") return "retry";
  switch (failure.reason) {
    case "configuration":
    // Both SSH causes are answered by the user's own SSH setup — known_hosts, or the key their
    // agent offers — never by signing in again to AFAR Connect and never by a retry that would
    // meet the same refusal.
    case "ssh-host-key":
    case "ssh-credential":
    case "unsupported":
      return "reconfigure";
    case "credential":
    case "revoked":
      return "sign-in";
  }
}

export interface EnvironmentPresentation {
  readonly entry: HostEnvironmentEntry;
  readonly connection: EnvironmentConnectionPresentation;
  readonly serverConfig: ServerConfig | null;
}

export function presentEnvironmentConnection(
  state: SupervisorConnectionState,
): EnvironmentConnectionPresentation {
  // `backoff` holds a scheduled `retryAt`; `offline` resumes when the network does.
  // `blocked` and a connection the user stopped wanting resume for nobody.
  const willRetryAutomatically =
    state.desired && (state.phase === "backoff" || state.phase === "offline");
  return {
    phase: clientReadiness(state),
    error: state.lastFailure?.message ?? null,
    traceId: state.lastFailure?.traceId ?? null,
    recoveryAction: recoveryAction(state),
    willRetryAutomatically,
    // The failure stays visible through the next attempt, which is still waiting on the same
    // endpoint, until that attempt reaches the Environment and synchronizes.
    endpointStarting:
      state.desired &&
      (state.phase === "backoff" ||
        (state.phase === "connecting" && state.stage !== "synchronizing")) &&
      state.lastFailure?._tag === "ConnectionTransientError" &&
      (state.lastFailure.reason === "environment-starting" ||
        state.lastFailure.reason === "endpoint-unavailable"),
  };
}

export function connectionStatusText(connection: EnvironmentConnectionPresentation): string {
  if (connection.endpointStarting) return "Starting... It connects once it is ready.";
  switch (connection.phase) {
    case "unavailable":
      return connection.error
        ? `Environment unavailable. Reason: ${connection.error}`
        : "Environment unavailable";
    case "starting":
      return connection.error ? `Starting... Reason: ${connection.error}` : "Starting...";
    case "synchronizing":
      return "Synchronizing...";
    case "ready":
      return "Ready";
  }
}

/** How a client offers recovery for a connection that is not ready. */
export interface EnvironmentRecoveryPresentation {
  readonly statusText: string;
  /** The connection is already working on it, so a retry control would be a no-op. */
  readonly isRecovering: boolean;
  /** Recovery is a retry the client performs in place, not a trip to settings. */
  readonly retriesInPlace: boolean;
  /** Waiting is enough: the connection is working on it or has a retry on its way. */
  readonly reconnectsOnItsOwn: boolean;
  /** What to tell the user about whether waiting is enough. */
  readonly recoveryDetail: string;
}

/**
 * What a surface offering recovery needs to know, beyond the status line every reader of
 * a connection already gets from `connectionStatusText`. Surfaces that only report status
 * keep using that; this is for the ones that also have to decide whether pressing
 * something would help, and whether it can help here or only in settings.
 */
export function presentEnvironmentRecovery(
  connection: EnvironmentConnectionPresentation,
): EnvironmentRecoveryPresentation {
  const isRecovering = connection.phase === "starting" || connection.phase === "synchronizing";
  const reconnectsOnItsOwn = isRecovering || connection.willRetryAutomatically;
  return {
    statusText: connectionStatusText(connection),
    isRecovering,
    retriesInPlace: connection.recoveryAction === null || connection.recoveryAction === "retry",
    reconnectsOnItsOwn,
    recoveryDetail: reconnectsOnItsOwn
      ? "Reconnecting on its own. You can retry now instead of waiting."
      : "This will not reconnect on its own.",
  };
}

function mapsEqual<K, V>(left: ReadonlyMap<K, V>, right: ReadonlyMap<K, V>): boolean {
  if (left.size !== right.size) {
    return false;
  }
  for (const [key, value] of left) {
    if (right.get(key) !== value) {
      return false;
    }
  }
  return true;
}

export function createEnvironmentPresentationAtoms<E>(input: {
  readonly environmentEntriesValueAtom: Atom.Atom<EnvironmentRuntimeState>;
  readonly stateAtom: (
    environmentId: EnvironmentId,
  ) => Atom.Atom<AsyncResult.AsyncResult<SupervisorConnectionState, E>>;
  /** Authoritative live server config, including streamed provider/settings updates. */
  readonly serverConfigValueAtom: (environmentId: EnvironmentId) => Atom.Atom<ServerConfig | null>;
}) {
  const presentationAtom = Atom.family((environmentId: EnvironmentId) =>
    Atom.make((get) => {
      const entry = get(input.environmentEntriesValueAtom).entries.get(environmentId);
      if (entry === undefined) {
        return null;
      }
      const state = Option.getOrElse(
        AsyncResult.value(get(input.stateAtom(environmentId))),
        () => AVAILABLE_CONNECTION_STATE,
      );
      return {
        entry,
        connection: presentEnvironmentConnection(state),
        serverConfig:
          clientReadiness(state) === "ready"
            ? get(input.serverConfigValueAtom(environmentId))
            : null,
      } satisfies EnvironmentPresentation;
    }).pipe(Atom.withLabel(`environment-presentation:${environmentId}`)),
  );

  let previous: ReadonlyMap<EnvironmentId, EnvironmentPresentation> = new Map();
  const presentationsAtom = Atom.make((get) => {
    const next = new Map<EnvironmentId, EnvironmentPresentation>();
    for (const environmentId of get(input.environmentEntriesValueAtom).entries.keys()) {
      const presentation = get(presentationAtom(environmentId));
      if (presentation !== null) {
        next.set(environmentId, presentation);
      }
    }
    if (mapsEqual(previous, next)) {
      return previous;
    }
    previous = next;
    return previous;
  }).pipe(Atom.withLabel("environment-presentations"));

  return {
    presentationAtom,
    presentationsAtom,
  };
}
